import { uniq } from "ramda";
import { Component } from "react";
import { connect } from "react-redux";
import { Form, type InjectedFormProps, SubmissionError, reduxForm } from "redux-form";

import {
  getFormError,
  getFormWarning,
  handleTabInForm,
  simpleDropdownFieldValidation,
} from "../../../app-utils/form.js";
import { pluralizeWord } from "../../../app-utils/string.js";
import { type Group, type Sample, type SampleStatus } from "../../../graphql.js";
import { updateSampleEditingState } from "../../../redux/modules/sample-editing.js";
import { loadSampleStatusesBySet } from "../../../redux/modules/sample.js";
import { type IAppState } from "../../../redux/reducer.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../../core/redux-form/ReactSelectField.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";

interface ISampleEditingStatusScreenOwnProps extends IPlaybookWizardStepProvidedProps {
  onNext?: () => Promise<Group | void>;
}

interface ISampleEditingStatusScreenAppStateProps {
  samples: Sample[];
  sampleEditingCount: number;
  globalFormError: string;
  globalFormWarning: string;
  initialValues: Partial<FormData>;
}

interface ISampleEditingStatusScreenDispatchProps {
  loadSampleStatuses: (statusSetId: number) => Promise<SampleStatus[]>;
  onValuesChange: (status: SampleStatus) => void; // called just before onNext with the selected values
}

export interface ISampleEditingStatusScreenProps
  extends ISampleEditingStatusScreenAppStateProps,
    ISampleEditingStatusScreenDispatchProps,
    ISampleEditingStatusScreenOwnProps,
    InjectedFormProps<FormData, any>,
    IPlaybookWizardStepInjectedProps {}

function mapStateToProps(state: IAppState): ISampleEditingStatusScreenAppStateProps {
  return {
    samples: state.sampleEditingState.editedSamples,
    sampleEditingCount: state.sampleEditingState.editingGrid.length,
    globalFormError: getFormError(SampleEditingStatusScreenImpl.SAMPLE_STATUS_FORM, state),
    globalFormWarning: getFormWarning(SampleEditingStatusScreenImpl.SAMPLE_STATUS_FORM, state),
    initialValues: {
      [SampleEditingStatusScreenImpl.FIELD_SAMPLE_STATUS]: SampleEditingStatusScreenImpl.NEUTRAL_STATUS,
    },
  };
}

function mapDispatchToProps(dispatch): ISampleEditingStatusScreenDispatchProps {
  return {
    loadSampleStatuses: (statusSetId) => dispatch(loadSampleStatusesBySet(statusSetId)),
    onValuesChange: (status: SampleStatus) => dispatch(updateSampleEditingState({ status })),
  };
}

interface ISampleEditingStatusScreenState {
  isLoadingStatuses?: boolean;
  statusSetIds?: number[];
  statuses?: SampleStatus[];
}

/**
 * Publishing screen, last screen of the editing wizard.
 */
class SampleEditingStatusScreenImpl extends Component<
  ISampleEditingStatusScreenProps,
  ISampleEditingStatusScreenState
> {
  public static SAMPLE_STATUS_FORM = "SAMPLE_STATUS_FORM";
  public static FIELD_SAMPLE_STATUS = "FIELD_SAMPLE_STATUS";

  public static NEUTRAL_STATUS: SampleStatus = { id: -1, name: "Do not change", statusClass: null };

  constructor(props) {
    super(props);
    this.state = {
      isLoadingStatuses: false,
      statusSetIds: [],
      statuses: [],
    };
  }

  componentDidMount() {
    const statusSetIds: number[] = uniq(this.props.samples.map((s) => s.type.statusSet.id));
    this.setState({ statusSetIds: statusSetIds });
    if (statusSetIds.length === 1) {
      this.setState({ isLoadingStatuses: true });
      this.props
        .loadSampleStatuses(statusSetIds[0])
        .then((statuses) => this.setState({ isLoadingStatuses: false, statuses }))
        .catch((error: Error) => this.props.updateWizardMessage(error.message));
    }
  }

  componentWillUnmount() {
    // allow to get rid of the submission error without touching any redux-form field (neither destroying the form)
    if (this.props.submitFailed) {
      this.props.reset();
    }
  }

  handleSubmitData = (values) => {
    const selectedStatus: SampleStatus = values[SampleEditingStatusScreenImpl.FIELD_SAMPLE_STATUS];
    if (selectedStatus === SampleEditingStatusScreenImpl.NEUTRAL_STATUS) {
      this.props.onValuesChange(null);
    } else {
      this.props.onValuesChange(selectedStatus);
    }

    if (this.props.onNext) {
      return this.props.onNext().then(
        (_fulfilled) => this.props.proceedWithNextStep && this.props.proceedWithNextStep(),
        (error: Error) => {
          throw new SubmissionError({ _error: error.message });
        },
      );
    } else {
      return this.props.proceedWithNextStep && this.props.proceedWithNextStep();
    }
  };

  /**
   * The status change drop down is disabled,
   * if the list of edited samples contains at least one sample type that disallows manual status change.
   * @return {any}
   */
  buildStatusDropDownMenu = () => {
    const statusChangeDisabledCount: number = this.props.samples.reduce(
      (prev, s) => prev + (s.type.allowManualStatusChange ? 0 : 1),
      0,
    );
    const sampleTypes: string[] = this.props.samples
      .filter((s) => !s.type.allowManualStatusChange)
      .map((s) => s.type.name);
    // reduce to distinct values
    const statusChangeDisabledSampleTypes: string[] = sampleTypes.filter((s, i) => sampleTypes.indexOf(s) === i);
    const tooltip =
      this.state.statusSetIds.length > 1
        ? "The selected samples have inconsistent status sets. Their status cannot be changed manually."
        : statusChangeDisabledCount === this.props.samples.length
          ? "The status of these samples cannot be changed manually."
          : statusChangeDisabledCount > 0
            ? "The status of one or more samples cannot be changed manually. To change the status, please remove all samples of type " +
              statusChangeDisabledSampleTypes.join(", ") +
              "."
            : `Select a new state for the samples or select '${SampleEditingStatusScreenImpl.NEUTRAL_STATUS.name}' to keep their current state`;
    const statusesOptions: SampleStatus[] = [SampleEditingStatusScreenImpl.NEUTRAL_STATUS, ...this.state.statuses];

    return (
      <ReactSelectReduxFormField
        name={SampleEditingStatusScreenImpl.FIELD_SAMPLE_STATUS}
        horizontalLayout={true}
        isClearable={false}
        isSearchable={true}
        isDisabled={statusChangeDisabledCount > 0}
        getOptionValue={(option: SampleStatus) => option.id}
        getOptionLabel={(option: SampleStatus) =>
          `${option.name} ${option.statusClass ? `("${option.statusClass.name}" class)` : ``}`
        }
        label={"Sample status"}
        tooltip={tooltip}
        placeholder={"Please select a sample status..."}
        component={ReactSelectField}
        options={statusesOptions}
        validate={simpleDropdownFieldValidation}
        isLoading={this.state.isLoadingStatuses}
      />
    );
  };

  render() {
    return (
      <PlaybookWizardStep
        {...this.props}
        playbookPage={{
          ...this.props.playbookPage,
          description: `Would you like to update the status of ${
            this.props.sampleEditingCount > 1 ? "these" : "this"
          } ${this.props.sampleEditingCount} ${pluralizeWord("sample", this.props.sampleEditingCount)}?`,
        }}
        wizardFooterData={{
          error: this.props.globalFormError || this.props.error,
          warning: this.props.globalFormWarning,
          nextButtonProps: {
            label: "Save",
            isLoading: !!this.props.asyncValidating,
            onPress: this.props.handleSubmit(this.handleSubmitData),
          },
        }}
      >
        <Form className="form-horizontal" onSubmit={this.props.handleSubmit(this.handleSubmitData)}>
          <div style={{ padding: "10px", marginBottom: "-10px" }} onKeyDownCapture={handleTabInForm}>
            {this.buildStatusDropDownMenu()}
          </div>
        </Form>
      </PlaybookWizardStep>
    );
  }
}

export default connect<
  ISampleEditingStatusScreenAppStateProps,
  ISampleEditingStatusScreenDispatchProps,
  ISampleEditingStatusScreenOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: SampleEditingStatusScreenImpl.SAMPLE_STATUS_FORM,
    destroyOnUnmount: false,
  })(SampleEditingStatusScreenImpl) as any,
);

export { SampleEditingStatusScreenImpl };
